import { Menu, ShoppingCart } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import logo from '../../asset/images/logo.png';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UserMenu } from '@/components/ui/user-menu';
import { useCartBadgeCount } from '@/hooks/useCartBadgeCount';
import { useNavigationService } from '@/services/navigate-service';
import { ROUTES } from '@/config/routes';
import { canCreateOnRoute } from '@/util/permissions';
import { cn } from '@/lib/utils';

export interface MobileHeaderProps {
  isLogin: boolean;
  onOpenNav: () => void;
}

/**
 * The <1024px app header: hamburger, logo, icon-only cart with badge, and
 * the user menu. Entity search and Prepay & Save live in MobileNavDrawer.
 * Every control meets the 44px touch floor asserted by the e2e suite.
 */
const MobileHeader = ({ isLogin, onOpenNav }: MobileHeaderProps) => {
  const { navigateTo } = useNavigationService();
  const location = useLocation();
  const { cartCount, showBadge } = useCartBadgeCount(isLogin);
  const isCartPage = location.pathname === ROUTES.ORDERS_CART;

  // relative z-40: keeps the UserMenu dropdown layered above <main>,
  // matching the desktop header's stacking context.
  return (
    <header className="relative z-40 flex h-[60px] w-full shrink-0 items-center justify-between border-b border-border bg-card px-1 text-foreground">
      <div className="flex min-w-0 items-center">
        <button
          onClick={onOpenNav}
          aria-label="Open navigation"
          className="flex h-11 w-11 shrink-0 items-center justify-center border-none bg-transparent text-foreground cursor-pointer"
        >
          <Menu className="h-6 w-6" />
        </button>
        <button
          onClick={() => window.location.assign('/home/properties')}
          aria-label="PlanOmatic home"
          className="flex h-11 min-w-11 items-center border-none bg-transparent px-1 cursor-pointer"
        >
          <img
            src={logo}
            alt="logo"
            className="h-8 w-auto max-w-[160px] object-contain"
          />
        </button>
      </div>

      {isLogin && (
        <div className="flex shrink-0 items-center pr-1">
          {/* Hidden for roles the cart route rejects, mirroring AppHeader —
              ProtectedRoute gates ROUTES.ORDERS_CART on the same predicate,
              so the button can never be a one-click path to /error (DV-2751). */}
          {canCreateOnRoute('Order') && (
            <div
              className={cn(
                'flex items-center',
                isCartPage && 'border-b-2 border-primary'
              )}
            >
              <Button
                variant="ghost"
                data-testid="Order cart"
                aria-label="Order cart"
                className="relative h-11 w-11 p-0"
                onClick={() => navigateTo(ROUTES.ORDERS_CART)}
              >
                <ShoppingCart className="h-5 w-5 text-foreground" />
                {showBadge && (
                  <Badge
                    variant="destructive"
                    data-testid="cart-badge"
                    className="absolute top-0 right-0 h-5 min-w-5 flex items-center justify-center rounded-full text-[10px] p-0.5"
                  >
                    {cartCount}
                  </Badge>
                )}
              </Button>
            </div>
          )}
          <UserMenu compact />
        </div>
      )}
    </header>
  );
};

export default MobileHeader;
